type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
};

type FullscreenVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
  webkitExitFullscreen?: () => void;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => void;
};

export type ToggleFullscreenOptions = {
  wrapper: HTMLElement | null;
  video?: HTMLVideoElement | null;
  preferNativeVideo?: boolean;
  immersiveActive: boolean;
  setImmersiveActive: (active: boolean) => void;
};

function getFullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function exitFullscreen(): void {
  const doc = document as FullscreenDocument;
  if (document.exitFullscreen) {
    void document.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    void doc.webkitExitFullscreen();
  }
}

function requestElementFullscreen(el: HTMLElement): void {
  if (el.requestFullscreen) {
    void el.requestFullscreen();
    return;
  }
  const webkitEl = el as FullscreenElement;
  if (webkitEl.webkitRequestFullscreen) {
    webkitEl.webkitRequestFullscreen();
  }
}

export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function supportsElementFullscreen(el?: HTMLElement | null): boolean {
  const target = el ?? (typeof document !== 'undefined' ? document.documentElement : null);
  if (!target) return false;
  const node = target as FullscreenElement;
  return !!(node.requestFullscreen || node.webkitRequestFullscreen);
}

export function setImmersiveFullscreenBodyLock(locked: boolean): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (locked) {
    document.body.style.overflow = 'hidden';
    // iOS Safari: immersive iken sayfa düzeyinde pinch-zoom / rubber-band çakışmasın
    document.body.style.touchAction = 'none';
    html.style.touchAction = 'none';
    html.style.overscrollBehavior = 'none';
  } else {
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
    html.style.touchAction = '';
    html.style.overscrollBehavior = '';
  }
}

/** iOS Safari: video native fullscreen; PDF/image ve fallback için CSS immersive mod. */
export function togglePlayerFullscreen(options: ToggleFullscreenOptions): void {
  const {
    wrapper,
    video,
    preferNativeVideo = true,
    immersiveActive,
    setImmersiveActive,
  } = options;

  if (immersiveActive) {
    setImmersiveActive(false);
    setImmersiveFullscreenBodyLock(false);
    return;
  }

  const fsEl = getFullscreenElement();
  if (fsEl) {
    exitFullscreen();
    return;
  }

  /* iOS custom player: webkitEnterFullscreen playsInline ile genelde no-op; immersive kullan */
  if (isIOSDevice()) {
    if (wrapper) {
      setImmersiveActive(true);
      setImmersiveFullscreenBodyLock(true);
    }
    return;
  }

  if (preferNativeVideo && video) {
    const nativeVideo = video as FullscreenVideo;
    if (nativeVideo.webkitDisplayingFullscreen) {
      return;
    }
    if (typeof nativeVideo.webkitEnterFullscreen === 'function') {
      try {
        nativeVideo.webkitEnterFullscreen();
        return;
      } catch {
        /* fall through */
      }
    }
    if (video.requestFullscreen) {
      try {
        void video.requestFullscreen();
        return;
      } catch {
        /* fall through */
      }
    }
  }

  if (!isIOSDevice() && wrapper && supportsElementFullscreen(wrapper)) {
    try {
      requestElementFullscreen(wrapper);
      return;
    } catch {
      /* fall through */
    }
  }

  if (wrapper) {
    setImmersiveActive(true);
    setImmersiveFullscreenBodyLock(true);
  }
}

export function isCoarsePointerDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

export function bindImmersiveFullscreenEscape(
  immersiveActive: boolean,
  onExit: () => void
): () => void {
  if (!immersiveActive || typeof window === 'undefined') {
    return () => undefined;
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onExit();
    }
  };

  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}

export function exitImmersiveFullscreen(setImmersiveActive: (active: boolean) => void): void {
  setImmersiveActive(false);
  setImmersiveFullscreenBodyLock(false);
}

/** iOS Safari: onClick bazen gecikir; pointerup ile çıkış daha güvenilir. */
export function handleImmersiveExitPointerEvent(
  event: { preventDefault(): void; stopPropagation(): void },
  onExit: () => void
): void {
  event.preventDefault();
  event.stopPropagation();
  onExit();
}
